// DatabaseSettingsRollupsTab.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import DatabaseSettingsRollupRow from './DatabaseSettingsRollupRow';
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';
import {
  normalizeRollupDefinitions,
  summarizeRollupDefinition,
} from 'rollups/rollupDefinitions';

const moveOrderedRollupById = (rollups, draggedId, targetId) => {
  const list = Array.isArray(rollups) ? [...rollups] : [];
  const fromIndex = list.findIndex((entry) => entry?.key === draggedId);
  const toIndex = list.findIndex((entry) => entry?.key === targetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;

  const [moved] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, moved);

  return list.map((entry, index) => ({
    ...entry,
    position: index,
  }));
};

/**
 * DatabaseSettingsRollupsTab
 *
 * Renders the rollups management tab inside database settings.
 *
 * @param {object[]} fields - Schema fields for the current type.
 * @param {function} onAddRollup - Opens the create-rollup editor.
 * @param {function} onEditRollup - Opens the edit-rollup editor.
 * @param {function} onReorderRollup - Persists a reordered rollup position.
 * @param {object[]} rollups - Saved rollups for the current type.
 *
 * @returns {ReactNode} The rendered rollups management tab.
 */
const DatabaseSettingsRollupsTab = React.memo(({
  fields = [],
  onAddRollup,
  onEditRollup,
  onReorderRollup,
  rollups = [],
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [orderedRollups, setOrderedRollups] = useState([]);
  const [draggingRollupId, setDraggingRollupId] = useState('');
  const [dragOverRollupId, setDragOverRollupId] = useState('');

  // ----- MEMOS ----- //

  const normalizedRollups = useMemo(() => normalizeRollupDefinitions(rollups), [rollups]);

  const fieldMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(fields) ? fields : []).forEach((field) => {
      const key = String(field?.name || field?.key || '').trim();
      if (!key) return;
      map.set(key, field);
    });
    return map;
  }, [fields]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setOrderedRollups(normalizedRollups);
  }, [normalizedRollups]);

  // ----- HANDLERS ----- //

  const handleDragStart = useCallback((rollup, event) => {
    const nextRollupId = String(rollup?.key || '').trim();
    if (!nextRollupId) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nextRollupId);
    setDraggingRollupId(nextRollupId);
  }, []);

  const handleDragOver = useCallback((rollup, event) => {
    const nextRollupId = String(rollup?.key || '').trim();
    if (!draggingRollupId || !nextRollupId || draggingRollupId === nextRollupId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverRollupId(nextRollupId);
  }, [draggingRollupId]);

  const handleDragEnd = useCallback(() => {
    setDraggingRollupId('');
    setDragOverRollupId('');
  }, []);

  const handleDrop = useCallback(async (rollup, event) => {
    const targetRollupId = String(rollup?.key || '').trim();
    if (!draggingRollupId || !targetRollupId || draggingRollupId === targetRollupId) {
      handleDragEnd();
      return;
    }

    event.preventDefault();

    const previousRollups = orderedRollups;
    const nextRollups = moveOrderedRollupById(previousRollups, draggingRollupId, targetRollupId);
    if (!nextRollups) {
      handleDragEnd();
      return;
    }

    setOrderedRollups(nextRollups);
    handleDragEnd();

    try {
      await onReorderRollup?.(draggingRollupId, targetRollupId);
    } catch {
      setOrderedRollups(previousRollups);
    }
  }, [draggingRollupId, handleDragEnd, onReorderRollup, orderedRollups]);

  // ----- RENDER ----- //

  return (
    <div className="database-modal-views">
      <DatabaseSettingsAddButton
        title="Add Rollup"
        onClick={() => onAddRollup?.()}
      />

      {orderedRollups.length > 0 ? (
        <div className="database-modal-view-list">
          {orderedRollups.map((rollup) => {
            const rollupId = String(rollup?.key || '').trim();

            return (
              <DatabaseSettingsRollupRow
                key={rollupId}
                isDragging={draggingRollupId === rollupId}
                isDropTarget={dragOverRollupId === rollupId}
                label={String(rollup?.title || '').trim() || 'Untitled rollup'}
                note={summarizeRollupDefinition(rollup, fieldMap)}
                onClick={() => onEditRollup?.(rollup)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => handleDragOver(rollup, event)}
                onDragStart={(event) => handleDragStart(rollup, event)}
                onDrop={(event) => handleDrop(rollup, event)}
                onSettingsClick={() => onEditRollup?.(rollup)}
              />
            );
          })}
        </div>
      ) : (
        <div
          className="database-modal-empty-state"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No rollups yet.
        </div>
      )}
    </div>
  );
});

// Dev Tools
DatabaseSettingsRollupsTab.displayName = 'DatabaseSettingsRollupsTab';

export default DatabaseSettingsRollupsTab;
